// The one list of public routes. Per-route metadata (pageMeta.js), the
// sitemap (public/sitemap.xml), and the react-snap pre-render list
// (package.json "reactSnap.include") all come from here, so a new page can't
// be added to one and forgotten in another. routes.test.js fails when the
// files drift; `npm run seo:sync` rewrites them.

import { GUIDES } from "./guides";
import { STORYBOARD_TEMPLATE_PAGES } from "./storyboardTemplates";

export const ORIGIN = "https://striply.xyz";

const STATIC_ROUTES = [
  {
    path: "/",
    priority: 1.0,
    title: "Striply — Free Comic Page Layout Editor",
    description:
      "Create beautiful comic page layouts in minutes. Drag to resize panels, add speech bubbles, upload artwork, and export print-ready PDFs. Free, no account required.",
  },
  {
    path: "/about",
    priority: 0.8,
    title: "About Striply — Why We Built a Comic Layout Tool",
    description:
      "Striply is a free comic page layout editor for aspiring comic artists, educators, and students. Learn who it's for and how it makes panel design fast and simple.",
  },
  {
    path: "/tutorial",
    priority: 0.8,
    title: "How to Lay Out a Comic Page — Striply Tutorial",
    description:
      "A step-by-step guide to designing comic pages in Striply: selecting and resizing panels, adding speech bubbles and sound effects, uploading art, and exporting print-ready PDFs.",
  },
  {
    path: "/guides",
    priority: 0.9,
    title: "Comic & Storyboard Guides — Layout, Lettering, Shots | Striply",
    description:
      "Craft-focused guides to making comics and storyboards: page layout, panel pacing, lettering, sound effects, print prep, camera shot types, and aspect ratios.",
  },
  {
    path: "/storyboard",
    priority: 0.9,
    title: "Free Online Storyboard Maker — No Sign-Up | Striply",
    description:
      "Make storyboards in your browser: aspect-locked 16:9, 2.39:1, and 9:16 frames, letterbox mattes and crop guides, shot notes under every frame, and print-ready PDF export. Free, no account.",
  },
  {
    path: "/storyboard-templates",
    priority: 0.9,
    title: "Free Storyboard Templates — 16:9, 2.39:1, 9:16 & Animation | Striply",
    description:
      "Free storyboard templates for video, film, vertical social video, and animation. Open any template in the online editor or download a blank printable PDF.",
  },
  {
    path: "/editor",
    priority: 0.7,
    title: "Comic Page Editor — Design & Export Panels | Striply",
    description:
      "Lay out comic pages in your browser: resize panels by dragging, drop in artwork, add speech bubbles and sound effects, then export a print-ready PDF. No sign-up needed.",
  },
  {
    path: "/storyboard/editor",
    priority: 0.6,
    title: "Storyboard Editor — Frames, Shot Notes & PDF Export | Striply",
    description:
      "The Striply storyboard editor: choose a frame aspect ratio, sketch or drop images into frames, type shot notes, and export your boards as a PDF.",
  },
  {
    path: "/collage",
    priority: 0.7,
    title: "Photo Collage Maker — Freeform Layouts | Striply",
    description:
      "Build freeform photo and image collages with drag-to-arrange panels, then export the finished layout. A flexible companion to Striply's comic page editor.",
  },
  {
    path: "/privacy",
    priority: 0.3,
    title: "Privacy Policy | Striply",
    description:
      "How Striply handles your data. We keep things minimal — read our privacy policy to see what we collect, how it's used, and how to request deletion.",
  },
  {
    path: "/terms",
    priority: 0.3,
    title: "Terms of Service | Striply",
    description:
      "The terms that govern your use of Striply, the free comic page layout editor. Read our acceptable-use rules and service terms.",
  },
];

const GUIDE_ROUTES = GUIDES.map((g) => ({
  path: `/guides/${g.slug}`,
  priority: 0.8,
  title: `${g.title} | Striply`,
  description: g.description,
}));

const STORYBOARD_TEMPLATE_ROUTES = STORYBOARD_TEMPLATE_PAGES.map((t) => ({
  path: `/storyboard-templates/${t.slug}`,
  priority: 0.8,
  title: t.metaTitle,
  description: t.description,
}));

/** Every public route: { path, priority, title, description }. */
export const SITE_ROUTES = [...STATIC_ROUTES, ...GUIDE_ROUTES, ...STORYBOARD_TEMPLATE_ROUTES];

export const ROUTE_META = Object.fromEntries(
  SITE_ROUTES.map(({ path, title, description }) => [path, { title, description }])
);

/** public/sitemap.xml contents. */
export const buildSitemapXml = () => [
  '<?xml version="1.0" encoding="UTF-8"?>',
  '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
  ...SITE_ROUTES.map((route) => [
    "  <url>",
    `    <loc>${ORIGIN}${route.path}</loc>`,
    `    <priority>${route.priority.toFixed(1)}</priority>`,
    "  </url>",
  ].join("\n")),
  "</urlset>",
  "",
].join("\n");

/** package.json "reactSnap.include". */
export const prerenderPaths = () => SITE_ROUTES.map((route) => route.path);
